import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Plus, Pencil, Trash2, Search, Car } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ['Autoturisme', 'SUV / Limuzine', 'XXL / +7 locuri'];

const categoryBadgeVariant = (category) => {
  if (category === 'XXL / +7 locuri') return 'destructive';
  if (category === 'SUV / Limuzine') return 'warning';
  return 'success';
};

const CategoryBadge = ({ category }) => {
  const colors = {
    'Autoturisme': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    'SUV / Limuzine': 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
    'XXL / +7 locuri': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors[category] || colors['Autoturisme']}`}>
      {category}
    </span>
  );
};

export default function CarCatalog() {
  const { token } = useAuth();
  const { language } = useLanguage();
  const isRo = language === 'ro';

  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [brandSearch, setBrandSearch] = useState('');
  const [modelSearch, setModelSearch] = useState('');

  // Add brand dialog
  const [addBrandOpen, setAddBrandOpen] = useState(false);
  const [newBrandName, setNewBrandName] = useState('');
  const [savingBrand, setSavingBrand] = useState(false);

  // Rename brand dialog
  const [renameBrandOpen, setRenameBrandOpen] = useState(false);
  const [renameBrandValue, setRenameBrandValue] = useState('');
  const [savingRenameBrand, setSavingRenameBrand] = useState(false);

  // Delete brand dialog
  const [deleteBrandOpen, setDeleteBrandOpen] = useState(false);

  // Add model dialog
  const [addModelOpen, setAddModelOpen] = useState(false);
  const [newModelName, setNewModelName] = useState('');
  const [newModelCategory, setNewModelCategory] = useState('Autoturisme');
  const [savingModel, setSavingModel] = useState(false);

  // Edit model dialog
  const [editModelOpen, setEditModelOpen] = useState(false);
  const [editingModel, setEditingModel] = useState(null);
  const [editModelName, setEditModelName] = useState('');
  const [editModelCategory, setEditModelCategory] = useState('Autoturisme');
  const [savingEditModel, setSavingEditModel] = useState(false);

  // Delete model dialog
  const [deleteModelOpen, setDeleteModelOpen] = useState(false);
  const [deletingModel, setDeletingModel] = useState(null);

  const authHeaders = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  const fetchCatalog = async () => {
    try {
      const res = await fetch(`${API_URL}/car-catalog`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setBrands(list);
      // Keep selectedBrand in sync after refresh
      if (selectedBrand) {
        const updated = list.find((b) => b.id === selectedBrand.id);
        setSelectedBrand(updated || null);
      }
    } catch (e) {
      console.error('Car catalog fetch error:', e);
      toast.error(isRo ? 'Eroare la încărcarea catalogului' : 'Failed to load catalog');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCatalog();
  }, []);

  const filteredBrands = brands.filter((b) =>
    b.brand.toLowerCase().includes(brandSearch.toLowerCase())
  );

  const filteredModels = selectedBrand
    ? (selectedBrand.models || []).filter((m) =>
        m.name.toLowerCase().includes(modelSearch.toLowerCase())
      )
    : [];

  // ---- Add Brand ----
  const handleAddBrand = async () => {
    if (!newBrandName.trim()) return;
    setSavingBrand(true);
    try {
      const res = await fetch(`${API_URL}/admin/car-catalog/brands`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ brand: newBrandName.trim() }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Error');
      }
      toast.success(isRo ? 'Brand adăugat cu succes' : 'Brand added successfully');
      setAddBrandOpen(false);
      setNewBrandName('');
      await fetchCatalog();
    } catch (e) {
      toast.error(e.message || (isRo ? 'Eroare la adăugare' : 'Failed to add brand'));
    } finally {
      setSavingBrand(false);
    }
  };

  // ---- Rename Brand ----
  const openRenameBrand = () => {
    setRenameBrandValue(selectedBrand.brand);
    setRenameBrandOpen(true);
  };

  const handleRenameBrand = async () => {
    if (!renameBrandValue.trim()) return;
    setSavingRenameBrand(true);
    try {
      const res = await fetch(`${API_URL}/admin/car-catalog/brands/${selectedBrand.id}`, {
        method: 'PATCH',
        headers: authHeaders,
        body: JSON.stringify({ brand: renameBrandValue.trim() }),
      });
      if (!res.ok) throw new Error();
      toast.success(isRo ? 'Brand redenumit cu succes' : 'Brand renamed successfully');
      setRenameBrandOpen(false);
      await fetchCatalog();
    } catch {
      toast.error(isRo ? 'Eroare la redenumire' : 'Failed to rename brand');
    } finally {
      setSavingRenameBrand(false);
    }
  };

  // ---- Delete Brand ----
  const handleDeleteBrand = async () => {
    try {
      const res = await fetch(`${API_URL}/admin/car-catalog/brands/${selectedBrand.id}`, {
        method: 'DELETE',
        headers: authHeaders,
      });
      if (!res.ok) throw new Error();
      toast.success(isRo ? 'Brand șters cu succes' : 'Brand deleted successfully');
      setDeleteBrandOpen(false);
      setSelectedBrand(null);
      await fetchCatalog();
    } catch {
      toast.error(isRo ? 'Eroare la ștergere' : 'Failed to delete brand');
    }
  };

  // ---- Add Model ----
  const handleAddModel = async () => {
    if (!newModelName.trim()) return;
    setSavingModel(true);
    try {
      const res = await fetch(`${API_URL}/admin/car-catalog/brands/${selectedBrand.id}/model`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ name: newModelName.trim(), category: newModelCategory }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Error');
      }
      toast.success(isRo ? 'Model adăugat cu succes' : 'Model added successfully');
      setAddModelOpen(false);
      setNewModelName('');
      setNewModelCategory('Autoturisme');
      await fetchCatalog();
    } catch (e) {
      toast.error(e.message || (isRo ? 'Eroare la adăugare' : 'Failed to add model'));
    } finally {
      setSavingModel(false);
    }
  };

  // ---- Edit Model ----
  const openEditModel = (model) => {
    setEditingModel(model);
    setEditModelName(model.name);
    setEditModelCategory(model.category);
    setEditModelOpen(true);
  };

  const handleEditModel = async () => {
    if (!editModelName.trim()) return;
    setSavingEditModel(true);
    try {
      const res = await fetch(`${API_URL}/admin/car-catalog/brands/${selectedBrand.id}/model`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({
          old_name: editingModel.name,
          new_name: editModelName.trim(),
          category: editModelCategory,
        }),
      });
      if (!res.ok) throw new Error();
      toast.success(isRo ? 'Model actualizat cu succes' : 'Model updated successfully');
      setEditModelOpen(false);
      await fetchCatalog();
    } catch {
      toast.error(isRo ? 'Eroare la actualizare' : 'Failed to update model');
    } finally {
      setSavingEditModel(false);
    }
  };

  // ---- Delete Model ----
  const openDeleteModel = (model) => {
    setDeletingModel(model);
    setDeleteModelOpen(true);
  };

  const handleDeleteModel = async () => {
    try {
      const res = await fetch(`${API_URL}/admin/car-catalog/brands/${selectedBrand.id}/model`, {
        method: 'DELETE',
        headers: authHeaders,
        body: JSON.stringify({ name: deletingModel.name }),
      });
      if (!res.ok) throw new Error();
      toast.success(isRo ? 'Model șters cu succes' : 'Model deleted successfully');
      setDeleteModelOpen(false);
      setDeletingModel(null);
      await fetchCatalog();
    } catch {
      toast.error(isRo ? 'Eroare la ștergere' : 'Failed to delete model');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center gap-3">
        <Car className="w-6 h-6 text-primary" />
        <h1 className="text-2xl font-bold">
          {isRo ? 'Catalog Mașini' : 'Car Catalog'}
        </h1>
      </div>

      <div className="flex gap-6 h-[calc(100vh-220px)] min-h-[500px]">
        {/* Left panel — brand list */}
        <div className="w-72 flex-shrink-0 flex flex-col border border-border rounded-lg overflow-hidden bg-card">
          <div className="p-4 border-b border-border space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={isRo ? 'Caută brand...' : 'Search brand...'}
                value={brandSearch}
                onChange={(e) => setBrandSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button
              size="sm"
              className="w-full"
              onClick={() => { setNewBrandName(''); setAddBrandOpen(true); }}
            >
              <Plus className="w-4 h-4 mr-2" />
              {isRo ? 'Adaugă brand' : 'Add Brand'}
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {filteredBrands.length === 0 ? (
              <p className="text-center text-muted-foreground text-sm py-8">
                {isRo ? 'Niciun brand găsit' : 'No brands found'}
              </p>
            ) : (
              filteredBrands.map((brand) => (
                <button
                  key={brand.id}
                  onClick={() => { setSelectedBrand(brand); setModelSearch(''); }}
                  className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors border-b border-border/50 last:border-0 ${
                    selectedBrand?.id === brand.id
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-accent'
                  }`}
                >
                  <span className="font-medium text-sm truncate">{brand.brand}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ml-2 flex-shrink-0 ${
                    selectedBrand?.id === brand.id
                      ? 'bg-primary-foreground/20 text-primary-foreground'
                      : 'bg-muted text-muted-foreground'
                  }`}>
                    {(brand.models || []).length}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Right panel — models */}
        <div className="flex-1 flex flex-col border border-border rounded-lg overflow-hidden bg-card">
          {!selectedBrand ? (
            <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-2">
              <Car className="w-12 h-12 opacity-30" />
              <p className="text-sm">{isRo ? 'Selectează un brand din stânga' : 'Select a brand from the left'}</p>
            </div>
          ) : (
            <>
              {/* Right panel header */}
              <div className="p-4 border-b border-border flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <h2 className="text-lg font-bold">{selectedBrand.brand}</h2>
                  <span className="text-sm text-muted-foreground">
                    {(selectedBrand.models || []).length} {isRo ? 'modele' : 'models'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={openRenameBrand}>
                    <Pencil className="w-4 h-4 mr-1" />
                    {isRo ? 'Redenumește' : 'Rename'}
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => setDeleteBrandOpen(true)}>
                    <Trash2 className="w-4 h-4 mr-1" />
                    {isRo ? 'Șterge brand' : 'Delete Brand'}
                  </Button>
                </div>
              </div>

              {/* Model search + add */}
              <div className="p-4 border-b border-border flex items-center gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder={isRo ? 'Caută model...' : 'Search model...'}
                    value={modelSearch}
                    onChange={(e) => setModelSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Button size="sm" onClick={() => { setNewModelName(''); setNewModelCategory('Autoturisme'); setAddModelOpen(true); }}>
                  <Plus className="w-4 h-4 mr-2" />
                  {isRo ? 'Adaugă model' : 'Add Model'}
                </Button>
              </div>

              {/* Models table */}
              <div className="flex-1 overflow-y-auto">
                {filteredModels.length === 0 ? (
                  <p className="text-center text-muted-foreground text-sm py-8">
                    {isRo ? 'Niciun model găsit' : 'No models found'}
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{isRo ? 'Model' : 'Model'}</TableHead>
                        <TableHead>{isRo ? 'Categorie' : 'Category'}</TableHead>
                        <TableHead className="text-right">{isRo ? 'Acțiuni' : 'Actions'}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredModels.map((model) => (
                        <TableRow key={model.name}>
                          <TableCell className="font-medium">{model.name}</TableCell>
                          <TableCell>
                            <CategoryBadge category={model.category} />
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openEditModel(model)}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-destructive hover:text-destructive"
                                onClick={() => openDeleteModel(model)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Add Brand Dialog */}
      <Dialog open={addBrandOpen} onOpenChange={setAddBrandOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isRo ? 'Adaugă brand' : 'Add Brand'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label>{isRo ? 'Nume brand' : 'Brand name'}</Label>
            <Input
              value={newBrandName}
              onChange={(e) => setNewBrandName(e.target.value)}
              placeholder={isRo ? 'ex: Volkswagen' : 'e.g. Volkswagen'}
              onKeyDown={(e) => e.key === 'Enter' && handleAddBrand()}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddBrandOpen(false)}>
              {isRo ? 'Anulează' : 'Cancel'}
            </Button>
            <Button onClick={handleAddBrand} disabled={savingBrand || !newBrandName.trim()}>
              {isRo ? 'Adaugă' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename Brand Dialog */}
      <Dialog open={renameBrandOpen} onOpenChange={setRenameBrandOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isRo ? 'Redenumește brand' : 'Rename Brand'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label>{isRo ? 'Nume nou' : 'New name'}</Label>
            <Input
              value={renameBrandValue}
              onChange={(e) => setRenameBrandValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleRenameBrand()}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameBrandOpen(false)}>
              {isRo ? 'Anulează' : 'Cancel'}
            </Button>
            <Button onClick={handleRenameBrand} disabled={savingRenameBrand || !renameBrandValue.trim()}>
              {isRo ? 'Salvează' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Brand Dialog */}
      <AlertDialog open={deleteBrandOpen} onOpenChange={setDeleteBrandOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{isRo ? 'Șterge brand' : 'Delete Brand'}</AlertDialogTitle>
            <AlertDialogDescription>
              {isRo
                ? `Ești sigur că vrei să ștergi brandul "${selectedBrand?.brand}" și toate modelele sale?`
                : `Are you sure you want to delete the brand "${selectedBrand?.brand}" and all its models?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{isRo ? 'Anulează' : 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteBrand} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {isRo ? 'Șterge' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add Model Dialog */}
      <Dialog open={addModelOpen} onOpenChange={setAddModelOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {isRo ? `Adaugă model la ${selectedBrand?.brand}` : `Add model to ${selectedBrand?.brand}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{isRo ? 'Nume model' : 'Model name'}</Label>
              <Input
                value={newModelName}
                onChange={(e) => setNewModelName(e.target.value)}
                placeholder={isRo ? 'ex: Golf' : 'e.g. Golf'}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label>{isRo ? 'Categorie' : 'Category'}</Label>
              <select
                value={newModelCategory}
                onChange={(e) => setNewModelCategory(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddModelOpen(false)}>
              {isRo ? 'Anulează' : 'Cancel'}
            </Button>
            <Button onClick={handleAddModel} disabled={savingModel || !newModelName.trim()}>
              {isRo ? 'Adaugă' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Model Dialog */}
      <Dialog open={editModelOpen} onOpenChange={setEditModelOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isRo ? 'Editează model' : 'Edit Model'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{isRo ? 'Nume model' : 'Model name'}</Label>
              <Input
                value={editModelName}
                onChange={(e) => setEditModelName(e.target.value)}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label>{isRo ? 'Categorie' : 'Category'}</Label>
              <select
                value={editModelCategory}
                onChange={(e) => setEditModelCategory(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModelOpen(false)}>
              {isRo ? 'Anulează' : 'Cancel'}
            </Button>
            <Button onClick={handleEditModel} disabled={savingEditModel || !editModelName.trim()}>
              {isRo ? 'Salvează' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Model Dialog */}
      <AlertDialog open={deleteModelOpen} onOpenChange={setDeleteModelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{isRo ? 'Șterge model' : 'Delete Model'}</AlertDialogTitle>
            <AlertDialogDescription>
              {isRo
                ? `Ești sigur că vrei să ștergi modelul "${deletingModel?.name}"?`
                : `Are you sure you want to delete the model "${deletingModel?.name}"?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{isRo ? 'Anulează' : 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteModel} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {isRo ? 'Șterge' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
